import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Link,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { tokens } from "../../theme";
import { PageHeader } from "../PageHeader";

/**
 * The Start & navigation page frame (NendaPortal_1, "Startvy" frames
 * 1748:20043 / 1751:20427 / 1756:21275): breadcrumbs, a section title, then
 * three columns - what you can pick, the editor for what
 * you picked, and how it looks on the TV.
 *
 * Every Start & navigation page uses it, so Home screen, Information and
 * Appearance read as one place rather than three editors that happen to share
 * a menu group.
 */

export interface Crumb {
  label: string;
  /** Absent on the last crumb - the page you are on. */
  to?: string;
}

export function StartNavLayout({
  crumbs,
  pageTitle,
  pageSubtitle,
  title,
  subtitle,
  list,
  editor,
  preview,
}: {
  crumbs: Crumb[];
  /** The sub-menu you are in - rendered in the app header. */
  pageTitle: string;
  pageSubtitle?: string;
  /** The level you are on inside it. */
  title: string;
  subtitle?: string;
  list: ReactNode;
  editor: ReactNode;
  /** Omitted for a page with nothing to preview; the editor takes its room. */
  preview?: ReactNode;
}) {
  return (
    <Box
      sx={{ px: { xs: 2, md: 4 }, py: { xs: 2.5, md: 3 } }}
      data-testid="startnav-page"
    >
      <PageHeader title={pageTitle} subtitle={pageSubtitle} />
      <Breadcrumbs
        separator={<ChevronRightIcon sx={{ fontSize: 18 }} />}
        aria-label="Breadcrumb"
        sx={{
          mb: 3,
          "& .MuiBreadcrumbs-separator": { color: tokens.brand, mx: 0.5 },
        }}
        data-testid="startnav-breadcrumbs"
      >
        {crumbs.map((c, i) =>
          c.to && i < crumbs.length - 1 ? (
            <Link
              key={c.label}
              component={RouterLink}
              to={c.to}
              underline="hover"
              sx={{ color: tokens.brand, fontSize: 15, fontWeight: 500 }}
            >
              {c.label}
            </Link>
          ) : (
            <Typography
              key={c.label}
              aria-current="page"
              sx={{
                color: tokens.brand,
                fontSize: 15,
                fontWeight: 500,
                borderBottom: "2px solid",
                borderColor: tokens.brand,
                lineHeight: 1.6,
              }}
            >
              {c.label}
            </Typography>
          )
        )}
      </Breadcrumbs>

      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 4 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h2"
            sx={{ fontSize: 22, fontWeight: 600, lineHeight: 1.3 }}
            data-testid="startnav-title"
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ mt: 0.75, fontSize: 14, color: tokens.inkMuted }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(220px, 280px) minmax(0, 1fr)",
            lg: preview
              ? "minmax(220px, 260px) minmax(0, 1fr) minmax(260px, 360px)"
              : "minmax(240px, 280px) minmax(0, 1fr)",
          },
          columnGap: 3,
          rowGap: 4,
          alignItems: "start",
        }}
      >
        <Box
          sx={{
            pr: { md: 3 },
            borderRight: { md: "1px solid" },
            borderColor: { md: tokens.divider },
            alignSelf: "stretch",
          }}
        >
          {list}
        </Box>
        <Box
          sx={{
            minWidth: 0,
            pr: { lg: preview ? 3 : 0 },
            borderRight: { lg: preview ? "1px solid" : "none" },
            borderColor: { lg: tokens.divider },
            alignSelf: "stretch",
          }}
          data-testid="startnav-editor"
        >
          {editor}
        </Box>
        {preview && (
          <Box
            sx={{
              minWidth: 0,
              // Below lg the preview drops under the editor, spanning both
              // columns, instead of squeezing the form into a strip.
              gridColumn: { md: "1 / -1", lg: "auto" },
              position: { lg: "sticky" },
              top: { lg: "calc(var(--foyer-app-header-h, 0px) + 16px)" },
            }}
          >
            {preview}
          </Box>
        )}
      </Box>
    </Box>
  );
}

/** A column's heading: a title and one muted line under it. */
export function PanelHeading({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography component="h3" sx={{ fontSize: 18, fontWeight: 600 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ mt: 0.5, fontSize: 13, color: tokens.inkMuted }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

/** A labelled field block inside the editor column (title + helper line). */
export function FieldBlock({
  label,
  help,
  children,
}: {
  label: string;
  help?: string;
  children: ReactNode;
}) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{label}</Typography>
      {help && (
        <Typography sx={{ mt: 0.25, fontSize: 13, color: tokens.inkMuted }}>
          {help}
        </Typography>
      )}
      <Box sx={{ mt: 1.5 }}>{children}</Box>
    </Box>
  );
}

/**
 * Two-way segmented choice - the design's "Ja / Nej" and "Celsius /
 * Fahrenheit". The selected half is filled with the primary colour.
 */
export function SegmentedChoice<T extends string>({
  value,
  options,
  onChange,
  disabled,
  testId,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (next: T) => void;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      disabled={disabled}
      onChange={(_e, next: T | null) => {
        if (next !== null) onChange(next);
      }}
      data-testid={testId}
      sx={{
        "& .MuiToggleButton-root": {
          minWidth: 80,
          height: 38,
          px: 2.5,
          textTransform: "none",
          fontSize: 14,
          fontWeight: 600,
          color: tokens.ink,
          borderColor: tokens.divider,
          bgcolor: tokens.surface,
        },
        "& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover":
          {
            bgcolor: tokens.brand,
            color: "#fff",
            borderColor: tokens.brand,
          },
      }}
    >
      {options.map((o) => (
        <ToggleButton
          key={o.value}
          value={o.value}
          data-testid={testId ? `${testId}-${o.value}` : undefined}
        >
          {o.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

/**
 * The "Shown on the home screen: Yes / No" row every switchable section starts
 * with (design: "Visas i startvyn"). Text on the left, the choice on the right.
 */
export function ActiveChoice({
  label,
  title = "Active",
  on,
  onChange,
  help,
  fixedNote,
}: {
  label: string;
  /** The row's heading. */
  title?: string;
  on: boolean;
  onChange?: (on: boolean) => void;
  help?: string;
  /** Replaces the control when the section cannot be switched. */
  fixedNote?: string;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={3}
      sx={{ mb: 4 }}
      data-testid="startnav-shown"
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{title}</Typography>
        <Typography sx={{ mt: 0.5, fontSize: 13, color: tokens.inkMuted }}>
          {help ?? `Controls whether ${label} is shown.`}
        </Typography>
      </Box>
      {fixedNote || !onChange ? (
        <Typography
          sx={{ fontSize: 13, color: tokens.inkMuted, flexShrink: 0 }}
          data-testid="startnav-fixed-note"
        >
          {fixedNote ?? "Always shown"}
        </Typography>
      ) : (
        <Box sx={{ flexShrink: 0 }}>
          <SegmentedChoice
            value={on ? "yes" : "no"}
            options={[
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ]}
            onChange={(v) => onChange(v === "yes")}
            testId="startnav-active"
          />
        </Box>
      )}
    </Stack>
  );
}

/** The tinted info callout ("Plats för väderdata"). */
export function InfoCallout({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        p: 2,
        borderRadius: "8px",
        border: "1px solid",
        borderColor: tokens.brand,
        bgcolor: tokens.brandTintSoft,
      }}
    >
      <InfoOutlinedIcon sx={{ color: tokens.brand, mt: 0.25 }} />
      <Box>
        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{title}</Typography>
        <Typography sx={{ mt: 0.25, fontSize: 13, color: tokens.inkMuted }}>
          {children}
        </Typography>
      </Box>
    </Stack>
  );
}

export interface ListItem {
  key: string;
  label: string;
  /** undefined = no status shown (a section that is not switchable). */
  on?: boolean;
  /** Can be dragged within its group. */
  orderable?: boolean;
  /**
   * Sections that live INSIDE this one (Information's Wi-Fi, Weather ...).
   * Shown nested under the row while it or one of them is selected, so the
   * rest of the list never disappears.
   */
  children?: ListItem[];
  /** Called with the children's orderable keys in their new order. */
  onReorderChildren?: (keys: string[]) => void;
}

export interface ListGroup {
  key: string;
  title: string;
  subtitle?: string;
  items: ListItem[];
  /** Called with the group's orderable keys in their new order. */
  onReorder?: (keys: string[]) => void;
}

/**
 * The left column: one or more titled groups of selectable rows, each with a
 * drag handle (when orderable) and an Active / Off status. Drag is the pointer
 * path; the keyboard gets dnd-kit's own sensor on the same handle.
 */
export function SectionList({
  groups,
  selected,
  onSelect,
}: {
  groups: ListGroup[];
  selected?: string;
  onSelect: (key: string) => void;
}) {
  return (
    <Stack spacing={4} data-testid="startnav-list">
      {groups.map((g) => (
        <SortableGroup
          key={g.key}
          group={g}
          selected={selected}
          onSelect={onSelect}
        />
      ))}
    </Stack>
  );
}

function SortableGroup({
  group,
  selected,
  onSelect,
}: {
  group: ListGroup;
  selected?: string;
  onSelect: (key: string) => void;
}) {
  return (
    <Box data-testid={`startnav-group-${group.key}`}>
      <PanelHeading
        title={`${group.title} (${group.items.length})`}
        subtitle={group.subtitle}
      />
      <SortableItems
        items={group.items}
        onReorder={group.onReorder}
        selected={selected}
        onSelect={onSelect}
      />
    </Box>
  );
}

/** One sortable run of rows - a group's, or the children nested under a row. */
function SortableItems({
  items,
  onReorder,
  selected,
  onSelect,
}: {
  items: ListItem[];
  onReorder?: (keys: string[]) => void;
  selected?: string;
  onSelect: (key: string) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const orderable = items.filter((i) => i.orderable).map((i) => i.key);
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id || !onReorder) return;
    const from = orderable.indexOf(String(active.id));
    const to = orderable.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    const next = orderable.slice();
    next.splice(from, 1);
    next.splice(to, 0, String(active.id));
    onReorder(next);
  };
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={orderable} strategy={verticalListSortingStrategy}>
        <Stack spacing={0.75}>
          {items.map((item) => {
            const open =
              !!item.children?.length &&
              (item.key === selected ||
                item.children.some((c) => c.key === selected));
            return (
              <Box key={item.key}>
                <SortableRow
                  item={item}
                  draggable={!!onReorder && !!item.orderable}
                  selected={item.key === selected}
                  expanded={item.children?.length ? open : undefined}
                  onSelect={() => onSelect(item.key)}
                />
                {open && (
                  <Box
                    sx={{
                      mt: 0.75,
                      ml: 3,
                      pl: 1.5,
                      borderLeft: "2px solid",
                      borderColor: tokens.divider,
                    }}
                    data-testid={`startnav-children-${item.key}`}
                  >
                    <SortableItems
                      items={item.children!}
                      onReorder={item.onReorderChildren}
                      selected={selected}
                      onSelect={onSelect}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({
  item,
  draggable,
  selected,
  expanded,
  onSelect,
}: {
  item: ListItem;
  draggable: boolean;
  selected: boolean;
  /** Set for a row with children: whether they are showing. */
  expanded?: boolean;
  onSelect: () => void;
}) {
  const s = useSortable({ id: item.key, disabled: !draggable });
  return (
    <Box
      ref={s.setNodeRef}
      role="button"
      tabIndex={0}
      aria-current={selected ? "true" : undefined}
      aria-expanded={expanded}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      data-testid={`startnav-item-${item.key}`}
      style={{
        transform: CSS.Transform.toString(s.transform),
        transition: s.transition,
        opacity: s.isDragging ? 0.5 : undefined,
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        minHeight: 50,
        px: 1.5,
        borderRadius: "8px",
        cursor: "pointer",
        bgcolor: selected ? tokens.brandSelected : "transparent",
        "&:hover": {
          bgcolor: selected ? tokens.brandSelected : tokens.brandTintSoft,
        },
        "&:focus-visible": {
          outline: `2px solid ${tokens.brand}`,
          outlineOffset: 1,
        },
      }}
    >
      <Box
        {...(draggable ? { ...s.attributes, ...s.listeners } : {})}
        aria-label={draggable ? `Drag ${item.label}` : undefined}
        onClick={(e) => e.stopPropagation()}
        sx={{
          display: "flex",
          color: tokens.inkFaint,
          cursor: draggable ? "grab" : "default",
          visibility: draggable ? "visible" : "hidden",
        }}
        data-testid={`startnav-drag-${item.key}`}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 15,
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {item.label}
      </Typography>
      {item.on !== undefined && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ flexShrink: 0 }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: item.on ? tokens.success : tokens.inkFaint,
            }}
          />
          <Typography
            sx={{
              fontSize: 11,
              color: item.on ? tokens.success : tokens.inkFaint,
            }}
          >
            {item.on ? "Active" : "Off"}
          </Typography>
        </Stack>
      )}
      {expanded !== undefined && (
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            color: tokens.inkFaint,
            flexShrink: 0,
            transform: expanded ? "rotate(180deg)" : "none",
            transition: "transform 150ms ease",
          }}
        />
      )}
    </Box>
  );
}
